import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    Inject,
    Input,
    NgZone,
    OnDestroy,
    OnInit,
    ViewChild,
} from '@angular/core';
import {Router} from '@angular/router';
import {I18NEXT_SERVICE, ITranslationService} from 'angular-i18next';
import {AuthService} from 'app/core/services/auth.service';
import {DomService} from 'app/core/services/dom.service';
import {IntercomService} from 'app/core/services/intercom.service';
import {AppUrls} from 'app/core/url/app-urls.service';
import {fromEvent, NEVER, Subscription} from 'rxjs';
import {distinctUntilChanged, map, switchMap} from 'rxjs/operators';
import {LayoutService} from 'app/core/services/layout.service';
import {DemoService} from 'app/core/services/demo.service';
import {AuthBridgeService} from 'app/features/auth-bridge-ext/services/auth-bridge.service';
import {UaSettingsService} from 'app/features/analytics/service/ua-settings.service';
import {SelectedAppService} from 'app/core/services/current-app/selected-app.service';
import {AppICanFetcher} from 'app/core/services/current-app/fetcher';
import {ApiService} from 'app/shared/modules/api/services/api.service';
import {LocalStorageService} from 'app/core/services/local-storage.service';

@Component({
    selector: 'modern-layout',
    templateUrl: './modern-layout.component.html',
    styleUrls: ['./modern-layout.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ModernLayoutComponent implements OnInit, OnDestroy {
    @Input() termsLink;
    @Input() buildInfo;
    @ViewChild('nav', {static: false}) nav: ElementRef<HTMLElement>;

    appUrls = AppUrls;

    navExpanded = false;
    private clearTimeout;

    fetcher: AppICanFetcher;

    get isScrolledContent() {
        return this._isScrolledContent || this.layout.forcedShowAsScrolled;
    }

    set isScrolledContent(v) {
        this._isScrolledContent = v;
    }

    get isOnboardingShowCreateApp() {
        return this.domService.isOnboardingShowCreateApp?.getValue();
    }

    get isUserAdmin() {
        return this.auth.getCurrentUserLastValue()?.permissions?.canViewAdminPanel;
    }

    get isInternalDashboard() {
        return document.location.pathname.includes('analytics/internal');
    }

    _isScrolledContent = false;
    private mediaQuerySubscription: Subscription;
    private scrollSub: Subscription;

    constructor(
        public auth: AuthService,
        private cd: ChangeDetectorRef,
        private domService: DomService,
        public intercom: IntercomService,
        @Inject(I18NEXT_SERVICE) public i18Next: ITranslationService,
        public layout: LayoutService,
        private router: Router,
        private zone: NgZone,
        private localStorage: LocalStorageService,
        public demo: DemoService,
        public bridgeService: AuthBridgeService,
        public uaSettings: UaSettingsService,
        public selectedAppService: SelectedAppService,
        api: ApiService
    ) {
        this.fetcher = new AppICanFetcher(api);
    }

    ngOnInit() {
        if (this.auth.currentUserId) {
            const userId = this.isUserAdmin ? (this.localStorage.getItem('user_id') ?? this.auth.currentUserId) : this.auth.currentUserId;
            const appId = this.localStorage.getItem('app_id');

            this.fetcher.loadAppsICanManage(userId).then(async apps => {
                if (!apps && !apps.nodes.length) {
                    return;
                }

                let nodes = apps.nodes;
                let currentUserApps = nodes.filter(app => app.ownerId === userId);
                this.selectedAppService.listUserApps$.next(currentUserApps);

                if (Boolean(currentUserApps.length)) {
                    let savedApp = currentUserApps.find(app => app.id === appId);
                    await this.selectedAppService.setSelectApp(savedApp ? savedApp.id : currentUserApps[0].id);
                    this.selectedAppService.AppsICanManage$.next(apps);
                }
            });
        }

        this.domService.isOnboardingShowCreateApp.next(this.auth.getCurrentUserLastValue()?.onboardingShowCreateApp);
        this.mediaQuerySubscription = this.domService
            .media('(min-width: 767px)')
            .pipe(switchMap(mql => (mql.matches ? NEVER : this.router.events)))
            .subscribe(() => {
                if (this.navExpanded) {
                    this.navExpanded = false;
                    this.cd.markForCheck();
                }
            });

        this.zone.runOutsideAngular(() => {
            this.scrollSub = fromEvent(window, 'scroll')
                .pipe(
                    // to avoid issues with retina display
                    // eslint-disable-next-line @typescript-eslint/no-magic-numbers
                    map(() => document.body.parentElement.scrollTop > 1.1),
                    distinctUntilChanged()
                )
                .subscribe(isScrolledContent => {
                    this.isScrolledContent = isScrolledContent;
                    this.cd.detectChanges();
                });
        });

        this.cd.markForCheck();
    }

    navigateToSignFromDemoUpPage() {
        document.location.href = 'https://app.appodeal.com/signup';
    }

    isPageWithoutHeader() {
        const pathName = document.location.pathname.slice(1);
        return (
            pathName === this.appUrls.SIGN_UP ||
            pathName === this.appUrls.SIGN_IN ||
            pathName === this.appUrls.REMIND_PASSWORD ||
            pathName === this.appUrls.SIGN_UP_SUCCESS
        );
    }

    isMonetizationPage() {
        return this.router.url.includes(this.appUrls.MONETIZATION);
    }

    isReportPage() {
        return this.router.url.includes(this.appUrls.ANALYTICS_REPORTS);
    }

    ngOnDestroy(): void {
        if (this.mediaQuerySubscription) {
            this.mediaQuerySubscription.unsubscribe();
        }
        if (this.scrollSub) {
            this.scrollSub.unsubscribe();
        }
        clearTimeout(this.clearTimeout);
    }
}
